import React, { useEffect, useRef, useState } from "react";

class Particle {
  constructor() {
    this.pos = { x: 0, y: 0 };
    this.vel = { x: 0, y: 0 };
    this.acc = { x: 0, y: 0 };
    this.target = { x: 0, y: 0 };
    this.closeEnoughTarget = 100;
    this.maxSpeed = 1.0;
    this.maxForce = 0.1;
    this.particleSize = 10;
    this.isKilled = false;
    this.startColor = { r: 0, g: 0, b: 0 };
    this.targetColor = { r: 0, g: 0, b: 0 };
    this.colorWeight = 0;
    this.colorBlendRate = 0.01;
  }

  move() {
    let proximityMult = 1;
    const distance = Math.sqrt(
      Math.pow(this.pos.x - this.target.x, 2) +
      Math.pow(this.pos.y - this.target.y, 2)
    );

    if (distance < this.closeEnoughTarget) {
      proximityMult = distance / this.closeEnoughTarget;
    }

    const towardsTarget = {
      x: this.target.x - this.pos.x,
      y: this.target.y - this.pos.y
    };

    const magnitude = Math.sqrt(
      towardsTarget.x * towardsTarget.x + towardsTarget.y * towardsTarget.y
    );

    if (magnitude > 0) {
      towardsTarget.x = (towardsTarget.x / magnitude) * this.maxSpeed * proximityMult;
      towardsTarget.y = (towardsTarget.y / magnitude) * this.maxSpeed * proximityMult;
    }

    const steer = {
      x: towardsTarget.x - this.vel.x,
      y: towardsTarget.y - this.vel.y
    };

    const steerMagnitude = Math.sqrt(steer.x * steer.x + steer.y * steer.y);

    if (steerMagnitude > 0) {
      steer.x = (steer.x / steerMagnitude) * this.maxForce;
      steer.y = (steer.y / steerMagnitude) * this.maxForce;
    }

    this.acc.x += steer.x;
    this.acc.y += steer.y;
    this.vel.x += this.acc.x;
    this.vel.y += this.acc.y;
    this.pos.x += this.vel.x;
    this.pos.y += this.vel.y;
    this.acc.x = 0;
    this.acc.y = 0;
  }

  draw(ctx, drawAsPoints) {
    if (this.colorWeight < 1.0) {
      this.colorWeight = Math.min(this.colorWeight + this.colorBlendRate, 1.0);
    }

    const currentColor = {
      r: Math.round(this.startColor.r + (this.targetColor.r - this.startColor.r) * this.colorWeight),
      g: Math.round(this.startColor.g + (this.targetColor.g - this.startColor.g) * this.colorWeight),
      b: Math.round(this.startColor.b + (this.targetColor.b - this.startColor.b) * this.colorWeight),
    };

    if (drawAsPoints) {
      ctx.fillStyle = `rgb(${currentColor.r},${currentColor.g},${currentColor.b})`;
      ctx.fillRect(this.pos.x, this.pos.y, 2, 2);
    } else {
      ctx.fillStyle = `rgb(${currentColor.r},${currentColor.g},${currentColor.b})`;
      ctx.beginPath();
      ctx.arc(this.pos.x, this.pos.y, this.particleSize / 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  kill(width, height) {
    if (!this.isKilled) {
      const randomPos = this.generateRandomPos(width / 2, height / 2, (width + height) / 2);
      this.target.x = randomPos.x;
      this.target.y = randomPos.y;
      this.targetColor = { r: 248, g: 250, b: 252 };
      this.colorWeight = 0;
      this.isKilled = true;
    }
  }

  generateRandomPos(x, y, mag) {
    const randomX = Math.random() * window.innerWidth;
    const randomY = Math.random() * window.innerHeight;
    const direction = { x: randomX - x, y: randomY - y };
    const magnitude = Math.sqrt(direction.x * direction.x + direction.y * direction.y);

    if (magnitude > 0) {
      direction.x = (direction.x / magnitude) * mag;
      direction.y = (direction.y / magnitude) * mag;
    }

    return { x: x + direction.x, y: y + direction.y };
  }

  distanceToTarget() {
    return Math.sqrt(
      Math.pow(this.pos.x - this.target.x, 2) +
      Math.pow(this.pos.y - this.target.y, 2)
    );
  }
}

const DEFAULT_WORDS = ["Welcome"];

export function ParticleTextEffect({ words = DEFAULT_WORDS, onComplete }) {
  const canvasRef = useRef(null);
  const animationRef = useRef();
  const particlesRef = useRef([]);
  const wordIndexRef = useRef(0);
  const waitFramesRef = useRef(0);
  const isCompleteRef = useRef(false);
  const wordFormedRef = useRef(false);
  const holdAfterFormedRef = useRef(0);
  const [isExiting, setIsExiting] = useState(false);

  const themeColors = [
    { r: 37, g: 99, b: 235 },
    { r: 79, g: 70, b: 229 },
    { r: 124, g: 58, b: 237 },
    { r: 59, g: 130, b: 246 }
  ];

  const nextWord = (word, canvas) => {
    const offscreenCanvas = document.createElement("canvas");
    offscreenCanvas.width = canvas.width;
    offscreenCanvas.height = canvas.height;
    const offscreenCtx = offscreenCanvas.getContext("2d");
    offscreenCtx.fillStyle = "black";

    const fontSize = window.innerWidth < 768 ? 60 : 120;
    offscreenCtx.font = `bold ${fontSize}px Inter, sans-serif`;
    offscreenCtx.textAlign = "center";
    offscreenCtx.textBaseline = "middle";
    offscreenCtx.fillText(word, canvas.width / 2, canvas.height / 2);

    const imageData = offscreenCtx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    const newColor = themeColors[wordIndexRef.current % themeColors.length];
    const particles = particlesRef.current;
    let particleIndex = 0;

    for (let i = 0; i < pixels.length; i += 24) {
      const alpha = pixels[i + 3];
      if (alpha > 0) {
        const x = (i / 4) % canvas.width;
        const y = Math.floor(i / 4 / canvas.width);

        let particle;
        if (particleIndex < particles.length) {
          particle = particles[particleIndex++];
          particle.isKilled = false;
        } else {
          particle = new Particle();
          const pOrigin = particle.generateRandomPos(canvas.width / 2, canvas.height / 2, 500);
          particle.pos.x = pOrigin.x;
          particle.pos.y = pOrigin.y;
          particle.maxSpeed = Math.random() * 14 + 24;
          particle.maxForce = 1.2;
          particles.push(particle);
          particleIndex++;
        }

        particle.target.x = x;
        particle.target.y = y;
        particle.targetColor = newColor;
      }
    }

    for (let i = particleIndex; i < particles.length; i++) {
      particles[i].kill(canvas.width, canvas.height);
    }

    wordFormedRef.current = false;
    holdAfterFormedRef.current = 0;
  };

  const checkIfFormed = () => {
    const particles = particlesRef.current;
    let settledCount = 0;
    let activeCount = 0;

    for (let i = 0; i < particles.length; i++) {
      if (!particles[i].isKilled) {
        activeCount++;
        if (particles[i].distanceToTarget() < 5) {
          settledCount++;
        }
      }
    }

    if (activeCount === 0) return false;
    return (settledCount / activeCount) > 0.85;
  };

  const animate = () => {
    const canvas = canvasRef.current;
    if (!canvas || isCompleteRef.current) return;

    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "rgba(248, 250, 252, 0.5)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const particles = particlesRef.current;
    for (let i = particles.length - 1; i >= 0; i--) {
      particles[i].move();
      particles[i].draw(ctx, true);
    }

    waitFramesRef.current++;

    // Check if current word is fully formed
    if (!wordFormedRef.current && waitFramesRef.current > 8) {
      if (checkIfFormed()) {
        wordFormedRef.current = true;
        holdAfterFormedRef.current = 0;
      }
    }

    // Once formed, hold for display time before transitioning
    if (wordFormedRef.current) {
      holdAfterFormedRef.current++;
    }

    const isLastWord = wordIndexRef.current === words.length - 1;
    // Faster: hold each word for ~3 frames, last word ~5 frames
    const holdThreshold = isLastWord ? 5 : 3;
    // Minimum frames before checking formation
    const minFrames = isLastWord ? 6 : 4;

    if (waitFramesRef.current >= minFrames && wordFormedRef.current && holdAfterFormedRef.current >= holdThreshold) {
      waitFramesRef.current = 0;

      if (wordIndexRef.current < words.length - 1) {
        wordIndexRef.current++;
        nextWord(words[wordIndexRef.current], canvas);
      } else {
        setIsExiting(true);
        setTimeout(() => {
          isCompleteRef.current = true;
          if (onComplete) onComplete();
        }, 250);
        return;
      }
    }

    animationRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    nextWord(words[0], canvas);
    animate();

    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  return (
    <div
      className={`fixed inset-0 z-[100] bg-slate-50 flex flex-col items-center justify-center transition-opacity duration-[400ms] ${
        isExiting ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
    </div>
  );
}